import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

const aboutFaqs = [
  {
    question: '欧美专区成立的初衷是什么？',
    answer: '欧美专区创立于2024年，致力于为中文用户提供最全面、最精选的欧美文化内容。我们希望通过专业的内容策划，帮助读者深入了解欧美时尚、音乐、影视、品牌和生活方式。',
  },
  {
    question: '欧美专区的内容团队有哪些背景？',
    answer: '我们的编辑团队由12位资深内容创作者组成，成员拥有欧美留学、工作和生活经验，涵盖时尚、音乐、影视、品牌营销等多个专业领域，确保每一篇文章都具备专业视角和深度。',
  },
  {
    question: '我可以向欧美专区投稿吗？',
    answer: '当然可以！我们欢迎对欧美文化有深入见解的作者加入。请通过联系我们表单提交您的稿件或合作意向，我们的编辑团队将在3个工作日内回复。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    try {
      const body = new URLSearchParams();
      body.append('name', (formData.get('name') as string || '').trim());
      body.append('email', (formData.get('email') as string || '').trim());
      body.append('subject', (formData.get('subject') as string || '').trim());
      body.append('message', (formData.get('message') as string || '').trim());
      const res = await fetch('https://readdy.ai/api/form/d9sq01llbrt1qcgaj80g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解欧美专区的品牌故事、编辑团队与使命愿景。欧美专区致力于为中文用户提供最全面的欧美文化、时尚、品牌、音乐与生活方式精选内容。"
        keywords="欧美专区,关于我们,品牌介绍,欧美内容,联系我们"
        canonicalUrl="https://www.oumeizone.com/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 | 欧美专区',
          url: 'https://www.oumeizone.com/about',
          description: '了解欧美专区的品牌故事、编辑团队与使命愿景。',
        }}
      />
      <SchemaOrg type="FAQPage" faqs={aboutFaqs} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Hero */}
            <section className="text-center py-12 md:py-16">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
                关于欧美专区
              </h1>
              <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                精选欧美内容一站汇聚，致力于成为中文世界最权威的欧美文化内容平台
              </p>
            </section>

            {/* Brand Story */}
            <section className="pb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    我们的故事
                  </h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                    <p>
                      欧美专区创立于2024年，由一群对欧美文化充满热情的内容创作者共同发起。我们深感中文互联网上关于欧美文化、欧美品牌和欧美生活方式的深度内容匮乏，因此决定打造一个专业的内容聚合平台。
                    </p>
                    <p>
                      从最初的个人博客到如今拥有12人专业编辑团队的精选内容平台，欧美专区始终秉持一个核心理念：<strong>为中文读者提供最真实、最深度的欧美内容体验</strong>。
                    </p>
                    <p>
                      我们的编辑团队分布在北京、上海、伦敦和纽约，每位成员都拥有丰富的欧美文化从业经验，涵盖时尚、音乐、影视、品牌营销等多个专业领域。正是这样多元化的团队背景，确保了欧美专区的内容兼具专业深度与全球视野。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden h-72 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20diverse%20team%20collaborating%2C%20warm%20natural%20lighting%2C%20minimalist%20office%20design%2C%20professional%20yet%20relaxed%20atmosphere%2C%20cream%20and%20wood%20tones%2C%20editorial%20photography%20style&width=800&height=600&seq=about-story-01&orientation=landscape"
                    alt="欧美专区编辑团队"
                    title="欧美专区编辑团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>

            {/* Mission & Values */}
            <section className="pb-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 py-16">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                  使命与价值
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      icon: 'ri-star-line',
                      title: '精选品质',
                      desc: '每一篇文章都经过严格筛选和深度编辑，确保内容质量达到行业领先水平。',
                    },
                    {
                      icon: 'ri-global-line',
                      title: '全球视野',
                      desc: '编辑团队遍布全球，为您带来最前沿的欧美文化、品牌和生活方式资讯。',
                    },
                    {
                      icon: 'ri-heart-line',
                      title: '用户至上',
                      desc: '始终以读者需求为核心，持续优化内容质量和阅读体验。',
                    },
                  ].map((item, idx) => (
                    <div key={idx} className="bg-background-50 rounded-lg p-6 text-center border border-background-200/70">
                      <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${item.icon} text-xl text-primary-600`}></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reputation / Milestones */}
            <section className="pb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                品牌声誉
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '536', label: '精选文章' },
                  { value: '89K+', label: '月活读者' },
                  { value: '98%', label: '好评率' },
                  { value: '50+', label: '合作品牌' },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">
                      {stat.value}
                    </div>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section id="contact" className="pb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-8">
                有任何问题或合作意向？欢迎通过以下表单与我们取得联系
              </p>

              <div className="max-w-lg mx-auto">
                {formStatus === 'success' ? (
                  <div className="bg-accent-100/70 border border-accent-300 rounded-lg p-6 text-center">
                    <i className="ri-check-double-line text-3xl text-accent-600 mb-3 block"></i>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      提交成功！
                    </h3>
                    <p className="text-sm text-foreground-600">
                      感谢您的留言，我们将在3个工作日内回复您。
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div className="visually-hidden-field">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-label text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-label text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-label text-foreground-700 mb-1.5">
                        主题 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-label text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-label text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                    {formStatus === 'error' && (
                      <p className="text-red-500 text-sm text-center">{formError}</p>
                    )}
                  </form>
                )}
              </div>
            </section>

            {/* FAQ */}
            <section className="pb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto space-y-3">
                {aboutFaqs.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                      <h4 className="font-label text-sm font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </h4>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}